import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ReactComponent as PencilIcon } from "assets/svgs/Editor/pencil.svg";
import FunctionalButton from "components/layout/FunctionalButton";
import EthernetConnectDialog from "components/control/settings/EthernetConnectDialog";
import EthernetProfileEditDialog from "components/control/settings/EthernetProfileEditDialog";
import { useARC } from "providers/ARC/ARCProvider";
import { useToast } from "providers/ToastProvider";
import {
    ETHERNET_CONNECT_GREEN_BUTTON_CLASS,
    ETHERNET_CONNECT_GREEN_TITLE_CLASS,
    NETWORK_ROW_DISCONNECT_BUTTON_CLASS,
    NETWORK_ROW_DISCONNECT_TITLE_CLASS,
} from "lib/consts/networkSettingsActionButtons";
import {
    deleteEthernetProfile,
    fetchEthernetProfiles,
    fetchEthernetStatus,
    getEthernetApiBaseUrl,
    postEthernetConnectProfile,
    postEthernetDisconnect,
    type EthernetSavedProfile,
    type EthernetStatusResponse,
} from "lib/ethernetApi";
import { formatLocalDateTime, LOCAL_DATETIME_MEDIUM_SHORT } from "lib/utils/dateTimeDisplay";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { toUserFacingApiError } from "lib/utils/userFacingApiError";

const STATUS_INTERVAL_MS = 5000;

const ROW_GRID =
    "grid w-full max-w-2xl grid-cols-[minmax(0,7.5rem)_minmax(0,1fr)] gap-x-3 gap-y-0 border-b border-black80 py-2 text-h5 last:border-b-0";

function Row({ label, value }: { label: string; value: string }) {
    return (
        <div className={ROW_GRID} role="row">
            <div className="text-black40" role="rowheader">
                {label}
            </div>
            <div className="min-w-0 break-words text-white" role="cell">
                {value}
            </div>
        </div>
    );
}

function isDeviceConnected(state: string | null | undefined): boolean {
    if (!state) return false;
    const s = state.toLowerCase();
    // "(disconnected)" and "deactivating" contain the substring "connected" — exclude first.
    if (s.includes("disconnect") || s.includes("unavailable") || s.includes("unmanaged")) {
        return false;
    }
    if (s.includes("connecting")) {
        return false;
    }
    return s.includes("connected");
}

function isDeviceConnecting(state: string | null | undefined): boolean {
    if (!state) return false;
    return state.toLowerCase().includes("connecting");
}

function formatProfileSummary(
    p: EthernetSavedProfile,
    t: (key: string) => string,
): string {
    const ac = p.autoconnect ? t("ethernet.autoConnectOn") : t("ethernet.autoConnectOff");
    if ((p.ipv4_method || "").toLowerCase() === "manual") {
        const addr = p.ipv4_address?.trim() || "—";
        return `${t("ethernet.manual")} · ${addr} · ${ac}`;
    }
    return `${t("ethernet.dhcp")} · ${ac}`;
}

const EthernetSettingsPanel: React.FC = () => {
    const { t } = useTranslation(["settings", "common"]);
    const { addToast } = useToast();
    const { selectedARC } = useARC();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const baseUrl = useMemo(
        () => getEthernetApiBaseUrl(selectedARC?.host ?? null, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const [status, setStatus] = useState<EthernetStatusResponse | null>(null);
    const [profiles, setProfiles] = useState<EthernetSavedProfile[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fetchInFlight = useRef(false);
    const [connectOpen, setConnectOpen] = useState(false);
    const [disconnectBusy, setDisconnectBusy] = useState(false);
    const [profileBusyId, setProfileBusyId] = useState<string | null>(null);
    const [profileBusyAction, setProfileBusyAction] = useState<"connect" | "remove" | null>(
        null
    );
    const [profileBeingEdited, setProfileBeingEdited] = useState<EthernetSavedProfile | null>(
        null
    );

    const runFetch = useCallback(async () => {
        if (!baseUrl || fetchInFlight.current) return;
        fetchInFlight.current = true;
        setLoading(true);
        setError(null);
        try {
            const [data, list] = await Promise.all([
                fetchEthernetStatus(baseUrl),
                fetchEthernetProfiles(baseUrl).catch(() => [] as EthernetSavedProfile[]),
            ]);
            setStatus(data);
            setProfiles(list);
        } catch (e) {
            setStatus(null);
            setError(toUserFacingApiError(e, t("ethernet.loadFailed")));
        } finally {
            fetchInFlight.current = false;
            setLoading(false);
        }
    }, [baseUrl, t]);

    useEffect(() => {
        if (!baseUrl) {
            setError(t("ethernet.hostNotSet"));
            setStatus(null);
            setProfiles([]);
            return;
        }
        setError(null);
        void runFetch();
        const id = window.setInterval(() => void runFetch(), STATUS_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [baseUrl, runFetch, t]);

    const handleDisconnect = useCallback(async () => {
        if (!baseUrl || disconnectBusy) return;
        setDisconnectBusy(true);
        try {
            await postEthernetDisconnect(baseUrl);
            addToast({ type: "success", message: t("ethernet.disconnected") });
            void runFetch();
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingApiError(e, t("ethernet.disconnectFailed")),
            });
        } finally {
            setDisconnectBusy(false);
        }
    }, [addToast, baseUrl, disconnectBusy, runFetch, t]);

    const handleConnectProfile = useCallback(
        async (profileId: string) => {
            if (!baseUrl || profileBusyId) return;
            setProfileBusyId(profileId);
            setProfileBusyAction("connect");
            try {
                await postEthernetConnectProfile(baseUrl, profileId);
                addToast({ type: "success", message: t("ethernet.connected") });
                void runFetch();
            } catch (e) {
                addToast({
                    type: "error",
                    message: toUserFacingApiError(e, t("ethernet.connectFailed")),
                });
            } finally {
                setProfileBusyId(null);
                setProfileBusyAction(null);
            }
        },
        [addToast, baseUrl, profileBusyId, runFetch, t]
    );

    const handleRemoveProfile = useCallback(
        async (profileId: string) => {
            if (!baseUrl || profileBusyId) return;
            setProfileBusyId(profileId);
            setProfileBusyAction("remove");
            try {
                await deleteEthernetProfile(baseUrl, profileId);
                addToast({ type: "success", message: t("ethernet.profileRemoved") });
                void runFetch();
            } catch (e) {
                addToast({
                    type: "error",
                    message: toUserFacingApiError(e, t("ethernet.removeFailed")),
                });
            } finally {
                setProfileBusyId(null);
                setProfileBusyAction(null);
            }
        },
        [addToast, baseUrl, profileBusyId, runFetch, t]
    );

    const hasApiError = status?.error != null && status.error !== "";
    const displayError =
        error ??
        (hasApiError
            ? toUserFacingApiError(status?.error, t("ethernet.loadFailed"))
            : null);
    const linked = isDeviceConnected(status?.state);
    const connecting = isDeviceConnecting(status?.state);
    const linkBusy = linked || connecting;

    const autoconnectLabel =
        status?.autoconnect === true ? t("common:yes") : status?.autoconnect === false ? t("common:no") : "—";

    return (
        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
            <div className="shrink-0 border-b border-black80 pb-4">
                <p className="text-h4 font-semibold text-white">{t("ethernet.title")}</p>
                {baseUrl && !displayError && status ? (
                    status.ipv4 ? (
                        <p className="mt-1 text-h5 text-primary2">
                            {t("ethernet.ipv4", { address: status.ipv4 })}
                        </p>
                    ) : (
                        <p className="mt-1 text-h5 text-black60">
                            {t("ethernet.noIpv4")}
                        </p>
                    )
                ) : null}

                {baseUrl && !displayError ? (
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                        <FunctionalButton
                            className={
                                linkBusy
                                    ? NETWORK_ROW_DISCONNECT_BUTTON_CLASS
                                    : ETHERNET_CONNECT_GREEN_BUTTON_CLASS
                            }
                            title={
                                disconnectBusy
                                    ? t("common:ellipsis")
                                    : linkBusy
                                      ? t("common:disconnect")
                                      : t("ethernet.createNewProfile")
                            }
                            titleClassName={
                                linkBusy
                                    ? NETWORK_ROW_DISCONNECT_TITLE_CLASS
                                    : ETHERNET_CONNECT_GREEN_TITLE_CLASS
                            }
                            disabled={!baseUrl || (linkBusy && disconnectBusy)}
                            onClick={() =>
                                linkBusy
                                    ? void handleDisconnect()
                                    : setConnectOpen(true)
                            }
                        />
                    </div>
                ) : null}
            </div>

            {displayError ? (
                <p className="mt-4 shrink-0 text-h5 text-red-400" role="alert">
                    {displayError}
                </p>
            ) : null}

            <div className="mt-4 min-w-0 shrink-0 rounded-r10 border border-black80 bg-black100 p-3">
                {!baseUrl ? null : loading && !status ? (
                    <div className="flex min-h-[12rem] flex-col items-center justify-center">
                        <p className="text-h5 text-black40">{t("common:loading")}</p>
                    </div>
                ) : status && !displayError ? (
                    <div
                        className="w-full"
                        role="table"
                        aria-label={t("ethernet.interfaceAriaLabel")}
                    >
                        <Row label={t("ethernet.state")} value={status.state?.trim() || "—"} />
                        <Row label={t("ethernet.autoConnect")} value={autoconnectLabel} />
                        <Row label="IPv4" value={status.ipv4?.trim() || "—"} />
                        <Row label={t("ethernet.gateway")} value={status.ipv4_gateway?.trim() || "—"} />
                    </div>
                ) : displayError ? null : (
                    <div className="flex min-h-[12rem] flex-col items-center justify-center">
                        <p className="text-h5 text-black60">{t("ethernet.noStatusData")}</p>
                    </div>
                )}
            </div>

            {baseUrl && !displayError ? (
                <div className="mt-4 shrink-0 border-t border-black80 pt-4">
                    <p className="text-h4 font-semibold text-white">{t("ethernet.historyProfiles")}</p>
                    <p className="mt-1 text-h5 text-black40">
                        {t("ethernet.historyProfilesDescription")}
                    </p>
                    {profiles.length === 0 ? (
                        <p className="mt-3 text-h5 text-black60">{t("ethernet.noSavedProfiles")}</p>
                    ) : (
                        <ul className="mt-3 flex max-h-[min(40vh,20rem)] flex-col gap-2 overflow-y-auto pr-1">
                            {profiles.map((p) => {
                                const busy = profileBusyId === p.id;
                                const connectBusy = busy && profileBusyAction === "connect";
                                const removeBusy = busy && profileBusyAction === "remove";
                                const profileLocked = !!profileBusyId;
                                return (
                                    <li
                                        key={p.id}
                                        className="flex items-center gap-3 rounded-r10 border border-black80 bg-black100 p-3"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="flex min-w-0 items-center gap-1.5">
                                                <p className="min-w-0 truncate text-h5 font-semibold text-white">
                                                    {p.display_name?.trim() ||
                                                        formatProfileSummary(p, t)}
                                                </p>
                                                <button
                                                    type="button"
                                                    className="shrink-0 rounded-r3 p-1 text-black40 hover:bg-black80 hover:text-white disabled:pointer-events-none disabled:opacity-40"
                                                    disabled={profileLocked || !baseUrl}
                                                    aria-label={t("ethernet.editProfile")}
                                                    onClick={() => setProfileBeingEdited(p)}
                                                >
                                                    <PencilIcon className="h-4 w-4 !h-4 !w-4 shrink-0 text-inherit" />
                                                </button>
                                            </div>
                                            {p.display_name?.trim() ? (
                                                <p className="mt-0.5 text-h5 text-black40">
                                                    {formatProfileSummary(p, t)}
                                                </p>
                                            ) : null}
                                            <p className="mt-0.5 text-h5 text-black40">
                                                {formatLocalDateTime(p.created_at, LOCAL_DATETIME_MEDIUM_SHORT)}
                                            </p>
                                        </div>
                                        <div className="flex shrink-0 items-center gap-2">
                                            <FunctionalButton
                                                className={ETHERNET_CONNECT_GREEN_BUTTON_CLASS}
                                                title={connectBusy ? t("common:ellipsis") : t("common:connect")}
                                                titleClassName={
                                                    ETHERNET_CONNECT_GREEN_TITLE_CLASS
                                                }
                                                disabled={
                                                    profileLocked ||
                                                    !baseUrl ||
                                                    linked ||
                                                    connecting
                                                }
                                                onClick={() => void handleConnectProfile(p.id)}
                                            />
                                            <FunctionalButton
                                                className={NETWORK_ROW_DISCONNECT_BUTTON_CLASS}
                                                title={removeBusy ? t("common:ellipsis") : t("common:remove")}
                                                titleClassName={
                                                    NETWORK_ROW_DISCONNECT_TITLE_CLASS
                                                }
                                                disabled={profileLocked || !baseUrl}
                                                onClick={() => void handleRemoveProfile(p.id)}
                                            />
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            ) : null}

            <EthernetConnectDialog
                visible={connectOpen}
                onClose={() => setConnectOpen(false)}
                baseUrl={baseUrl}
                onDone={() => void runFetch()}
            />
            <EthernetProfileEditDialog
                profile={profileBeingEdited}
                baseUrl={baseUrl}
                onClose={() => setProfileBeingEdited(null)}
                onDone={() => void runFetch()}
            />
        </div>
    );
};

export default EthernetSettingsPanel;
